import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const CATEGORIES = ["Hoodies", "Tees", "Outerwear", "Pants", "Accessories"];

const EMPTY_FORM = {
  name: "", category: "Hoodies", price: "", original_price: "", tag: "",
  image: "", image_alt: "", sizes: "S, M, L, XL", colors: "", description: "", in_stock: true,
};

export const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category, price: p.price, original_price: p.original_price || "",
      tag: p.tag || "", image: p.image, image_alt: p.image_alt || "",
      sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "),
      description: p.description || "", in_stock: p.in_stock,
    });
    setEditing(p);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Product added to the store");
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleStock = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, {
        name: p.name, category: p.category, price: p.price, original_price: p.original_price,
        tag: p.tag, image: p.image, image_alt: p.image_alt, sizes: p.sizes, colors: p.colors || [],
        description: p.description, in_stock: !p.in_stock,
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const field = (key, label, props = {}) => (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">{label}</span>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm transition-colors"
        data-testid={`product-form-${key}`}
        {...props}
      />
    </label>
  );

  return (
    <div data-testid="products-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight uppercase">Merchandise ({products.length})</h2>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#E05A36] text-[#0C0A09] font-bold uppercase text-xs tracking-wider px-5 py-3 hover:bg-[#F06B47] transition-colors" data-testid="add-product-button">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      <div className="overflow-x-auto border border-[#2C2623]">
        <table className="w-full text-sm" data-testid="products-table">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067] border-b border-[#2C2623]">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2C2623]">
            {products.map((p) => (
              <tr key={p.id} className="hover:bg-[#1E1A17] transition-colors" data-testid={`product-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="w-10 h-12 object-cover" />
                    <div>
                      <p className="font-bold">{p.name}</p>
                      {p.tag && <p className="text-[10px] font-mono text-[#E05A36] uppercase">{p.tag}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#B8AEA5]">{p.category}</td>
                <td className="px-4 py-3 font-mono text-[#E29D47]">${p.price.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleStock(p)}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors ${p.in_stock ? "text-[#7BC47F] border-[#7BC47F]/40" : "text-[#E05A36] border-[#E05A36]/40"}`}
                    data-testid={`stock-toggle-${p.id}`}
                  >
                    {p.in_stock ? "In stock" : "Sold out"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} className="p-2 text-[#B8AEA5] hover:text-[#E05A36] transition-colors" data-testid={`edit-product-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 text-[#B8AEA5] hover:text-[#E05A36] transition-colors" data-testid={`delete-product-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {editing && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)} className="fixed inset-0 bg-[#0C0A09]/75 backdrop-blur-sm z-50" />
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              className="fixed inset-x-4 top-[5%] bottom-[5%] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[560px] bg-[#171412] border border-[#2C2623] z-50 overflow-y-auto p-6"
              data-testid="product-form-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display font-bold uppercase tracking-tight">{editing === "new" ? "Add product" : "Edit product"}</h3>
                <button onClick={() => setEditing(null)} className="text-[#7A7067] hover:text-[#E05A36]" data-testid="product-form-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={save} className="space-y-4" data-testid="product-form">
                {field("name", "Name", { required: true })}
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Category</span>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 w-full bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm transition-colors"
                    data-testid="product-form-category"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {field("price", "Price ($)", { required: true, type: "number", step: "0.01", min: "0" })}
                  {field("original_price", "Compare-at price ($)", { type: "number", step: "0.01", min: "0" })}
                </div>
                {field("tag", "Tag (e.g. NEW DROP, LIMITED)")}
                {field("image", "Image URL", { required: true, type: "url" })}
                {field("image_alt", "Hover / alternate image URL", { type: "url" })}
                {field("sizes", "Sizes (comma separated)")}
                {field("colors", "Colors (comma separated, optional — e.g. Black, White, Blue)")}
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">Description</span>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="mt-1 w-full bg-[#0C0A09] border border-[#2C2623] focus:border-[#E05A36] outline-none px-3 py-2.5 text-sm transition-colors resize-none"
                    data-testid="product-form-description"
                  />
                </label>
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={form.in_stock}
                    onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                    className="accent-[#E05A36] w-4 h-4"
                    data-testid="product-form-in-stock"
                  />
                  In stock
                </label>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#E05A36] text-[#0C0A09] font-bold uppercase tracking-wider py-3.5 hover:bg-[#F06B47] active:scale-[0.98] transition-all disabled:opacity-50"
                  data-testid="product-form-save"
                >
                  {saving ? "Saving..." : editing === "new" ? "Add to store" : "Save changes"}
                </button>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
